/**
 * ResizeManager - Centralized window resize event handler
 * 
 * This utility provides a single point of control for all resize events
 * in the application, preventing multiple uncoordinated resize handlers
 * that can cause performance issues and page freezing.
 * 
 * Features:
 * - Singleton pattern for global coordination
 * - Throttling with requestAnimationFrame for optimal performance
 * - Handles both resize and orientationchange events
 * - Safe callback execution with error handling
 * - Memory leak prevention with proper cleanup
 */
class ResizeManager {
  constructor() {
    this.callbacks = new Set();
    this.isThrottled = false;
    this.throttleDelay = 250;
    this.rafId = null;
    this.timeoutId = null;
    this.isInitialized = false;
    
    // Bind methods to ensure correct context
    this.handleResize = this.handleResize.bind(this);
    this.handleOrientationChange = this.handleOrientationChange.bind(this);
    this.executeCallbacks = this.executeCallbacks.bind(this);
    
    this.init();
  }

  /**
   * Initialize the resize manager
   * Sets up event listeners for resize and orientation change
   */
  init() {
    if (this.isInitialized) {
      console.warn('ResizeManager already initialized');
      return;
    }

    // Add resize listener with passive flag for better performance
    window.addEventListener('resize', this.handleResize, { 
      passive: true,
      capture: false 
    });
    
    // Handle orientation change separately for mobile devices
    window.addEventListener('orientationchange', this.handleOrientationChange, { 
      passive: true,
      capture: false 
    });

    this.isInitialized = true;
    console.log('🔄 ResizeManager initialized - centralized resize handling active');
  }

  /**
   * Handle window resize events with throttling
   */
  handleResize() {
    if (this.isThrottled) {
      return;
    }
    
    this.isThrottled = true;
    
    // Cancel any pending RAF
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }
    
    // Use requestAnimationFrame for smooth performance
    this.rafId = requestAnimationFrame(() => {
      this.executeCallbacks('resize');
      
      // Reset throttle after delay
      this.timeoutId = setTimeout(() => {
        this.isThrottled = false;
      }, this.throttleDelay);
    });
  }

  /**
   * Handle orientation change events
   * Orientation changes need special handling due to timing
   */
  handleOrientationChange() {
    // Clear any existing throttle for orientation change
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    
    // Orientation change needs a slight delay to get accurate viewport dimensions
    setTimeout(() => {
      this.executeCallbacks('orientationchange');
    }, 100);
  }

  /**
   * Execute all registered callbacks safely
   * @param {string} eventType - Type of event ('resize' or 'orientationchange')
   */
  executeCallbacks(eventType = 'resize') {
    if (this.callbacks.size === 0) {
      return;
    }

    console.log(`🔄 ResizeManager executing ${this.callbacks.size} callback(s) for ${eventType}`);
    
    this.callbacks.forEach(callback => {
      try {
        if (typeof callback === 'function') {
          callback(eventType);
        }
      } catch (error) {
        console.error('ResizeManager callback error:', error);
        // Don't remove the callback on error - let the component handle it
      }
    });
  }

  /**
   * Register a callback function for resize events
   * @param {Function} callback - Function to call on resize
   * @returns {boolean} - Success status
   */
  register(callback) {
    if (typeof callback !== 'function') {
      console.error('ResizeManager.register: callback must be a function');
      return false;
    }

    if (this.callbacks.has(callback)) {
      console.warn('ResizeManager.register: callback already registered');
      return false;
    }

    this.callbacks.add(callback);
    console.log(`✅ ResizeManager registered callback (total: ${this.callbacks.size})`);
    return true;
  }

  /**
   * Unregister a callback function
   * @param {Function} callback - Function to remove
   * @returns {boolean} - Success status
   */
  unregister(callback) {
    if (typeof callback !== 'function') {
      console.error('ResizeManager.unregister: callback must be a function');
      return false;
    }

    const removed = this.callbacks.delete(callback);
    if (removed) {
      console.log(`❌ ResizeManager unregistered callback (total: ${this.callbacks.size})`);
    } else {
      console.warn('ResizeManager.unregister: callback was not registered');
    }
    
    return removed;
  }

  /**
   * Get current callback count for debugging
   * @returns {number} - Number of registered callbacks
   */
  getCallbackCount() {
    return this.callbacks.size;
  }

  /**
   * Force execute all callbacks (for testing/debugging)
   * @param {string} eventType - Type of event to simulate
   */
  forceExecute(eventType = 'resize') {
    console.log('🔧 ResizeManager force executing callbacks');
    this.executeCallbacks(eventType);
  }

  /**
   * Cleanup method for proper memory management
   * Should be called when the application is shutting down
   */
  cleanup() {
    // Cancel any pending operations
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }

    // Remove event listeners
    if (this.isInitialized) {
      window.removeEventListener('resize', this.handleResize);
      window.removeEventListener('orientationchange', this.handleOrientationChange);
      this.isInitialized = false;
    }

    // Clear all callbacks
    const callbackCount = this.callbacks.size;
    this.callbacks.clear();
    
    console.log(`🧹 ResizeManager cleanup completed (removed ${callbackCount} callbacks)`);
  }

  /**
   * Get debug information about the ResizeManager state
   * @returns {Object} - Debug information
   */
  getDebugInfo() {
    return {
      isInitialized: this.isInitialized,
      callbackCount: this.callbacks.size,
      isThrottled: this.isThrottled,
      throttleDelay: this.throttleDelay,
      hasRAF: !!this.rafId,
      hasTimeout: !!this.timeoutId
    };
  }
}

// Create and export singleton instance
const resizeManager = new ResizeManager();

// Cleanup on page unload to prevent memory leaks
window.addEventListener('beforeunload', () => {
  resizeManager.cleanup();
});

export default resizeManager;
